/** Modelli delle App del cantiere (contracts/api-site-apps.md). */

export type SiteAppState =
  'active' | 'inactive' | 'activatable' | 'not_in_plan' | 'coming_soon' | 'retired';

export type ReadOnlyReason =
  | 'site_archived'
  | 'site_over_plan_limit'
  | 'entitlement_missing'
  | 'app_retired'
  | 'preview_ended';

export interface ReadOnlyView {
  readonly reason: ReadOnlyReason;
  readonly message: string;
}

export interface RetirementView {
  readonly readOnlyFrom: string | null;
}

export interface SiteCatalogApp {
  readonly code: string;
  readonly glyph: string;
  readonly name: string;
  readonly summary: string;
  readonly description: string;
  readonly audience: readonly string[];
  readonly version: string;
  readonly state: SiteAppState;
  readonly stateMessage: string | null;
  readonly myRole: string | null;
  readonly readOnly: ReadOnlyView | null;
  readonly commercialNote: string | null;
  readonly retirement: RetirementView | null;
  /** L'App è attiva senza Amministratore: l'Amministratore del cantiere può designarlo. */
  readonly needsAdmin: boolean;
}

export interface SiteCatalogArea {
  readonly code: string;
  readonly name: string;
  readonly description: string;
  readonly icon: string;
  readonly apps: readonly SiteCatalogApp[];
}

export interface SiteCatalog {
  readonly canManage: boolean;
  readonly areas: readonly SiteCatalogArea[];
}

export interface AppRoleRef {
  readonly code: string;
  readonly name: string;
}

export interface AppContext {
  readonly code: string;
  readonly glyph: string;
  readonly name: string;
  readonly version: string;
  readonly releaseNotes: string;
  readonly myRole: AppRoleRef;
  readonly allowedActions: readonly string[];
  readonly readOnly: ReadOnlyView | null;
  readonly retirement: RetirementView | null;
}

export interface AppCandidate {
  readonly userId: string;
  readonly name: string;
  readonly initials: string;
  readonly professionalRoles: readonly string[];
}

export interface AppRoleView {
  readonly code: string;
  readonly name: string;
  readonly description: string;
  readonly requiredProfessionalRoles: readonly string[];
}

export type AppMemberSiteStatus = 'active' | 'suspended' | 'removed' | 'left';

export interface AppMemberView {
  readonly userId: string;
  readonly name: string;
  readonly initials: string;
  readonly professionalRoles: readonly string[];
  readonly siteAccess: 'direct' | 'project' | null;
  readonly role: string;
  readonly requirementMissing: boolean;
  readonly siteStatus: AppMemberSiteStatus;
}

export interface AppMembers {
  readonly canManage: boolean;
  readonly readOnly: ReadOnlyView | null;
  readonly roles: readonly AppRoleView[];
  readonly members: readonly AppMemberView[];
  readonly candidates: readonly AppCandidate[];
}

/** Etichette degli stati dell'App nel catalogo del cantiere. */
export const APP_STATE_LABELS: Readonly<Record<SiteAppState, string>> = {
  active: 'Attiva',
  inactive: 'Disattivata',
  activatable: 'Attivabile',
  not_in_plan: 'Non inclusa nel piano',
  coming_soon: 'In arrivo',
  retired: 'Ritirata',
};

/** Le App attive in cui l'utente ha un ruolo, in ordine di catalogo. */
export function accessibleApps(catalog: SiteCatalog | null): readonly SiteCatalogApp[] {
  return (catalog?.areas ?? [])
    .flatMap((area) => area.apps)
    .filter((app) => app.state === 'active' && app.myRole !== null);
}
